import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Tool, RewardActivity } from '../types';
import { rewardActivities, recordToolUse } from '../data/users';
import { tools, getFeaturedTool } from '../data/tools';
import { 
  getTokenBalance,
  canClaimTokens,
  getNextClaimTime,
  claimDailyTokens,
  getClaimStreak,
  setTokenBalance as setStorageTokenBalance,
  handleToolClick,
  getToolStreak,
  getLastToolClick
} from '../lib/cookies';

interface AppContextType {
  user: User | null;
  loading: boolean;
  tools: Tool[];
  featuredTool: Tool;
  rewardActivities: RewardActivity[];
  canClaimReward: boolean;
  nextClaimTime: Date | null;
  tokenBalance: number;
  claimStreak: number;
  toolStreak: number;
  handleClaimDailyReward: () => void;
  handleUseTool: (toolId: string) => Promise<void>;
  handleMarkToolAsUsed: (toolId: string) => void;
  setTokenBalance: (amount: number) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState<RewardActivity[]>([]);
  const [canClaimReward, setCanClaimReward] = useState(false);
  const [nextClaimTime, setNextClaimTime] = useState<Date | null>(null);
  const [featuredTool, setFeaturedTool] = useState<Tool>(getFeaturedTool());
  const [tokenBalance, setTokenBalanceState] = useState(getTokenBalance());
  const [claimStreak, setClaimStreak] = useState(getClaimStreak());
  const [toolStreak, setToolStreak] = useState(getToolStreak());

  useEffect(() => {
    // Set loading to false since we're not fetching user data anymore
    setLoading(false);
  }, []);

  useEffect(() => {
    const updateStatus = () => {
      const canClaim = canClaimTokens();
      setCanClaimReward(canClaim);
      setNextClaimTime(getNextClaimTime());
      const currentBalance = getTokenBalance();
      setTokenBalanceState(currentBalance);
      setClaimStreak(getClaimStreak());
      setToolStreak(getToolStreak());
    };

    updateStatus();
    const intervalId = setInterval(updateStatus, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const handleClaimDailyReward = () => {
    if (canClaimReward) {
      const { newBalance, streak, bonus } = claimDailyTokens();
      
      setTokenBalanceState(newBalance);
      setStorageTokenBalance(newBalance);
      setClaimStreak(streak);
      setCanClaimReward(false);
      
      const nextTime = new Date();
      nextTime.setHours(nextTime.getHours() + 24);
      setNextClaimTime(nextTime);

      const activity: RewardActivity = {
        id: `ra${activities.length + 1}`,
        userId: user?.id || 'anonymous',
        description: bonus > 0 
          ? `Daily reward claimed (7-day streak bonus: +${bonus})`
          : `Daily login reward (${streak} day streak)`,
        amount: 50 + bonus,
        timestamp: new Date().toISOString()
      };
      
      setActivities(prev => [activity, ...prev]);
    }
  };

  const handleUseTool = async (toolId: string) => {
    const tool = tools.find(t => t.id === toolId);
    if (!tool) return;
    
    // Handle anonymous user tool click
    const { streak } = handleToolClick();
    setToolStreak(streak);
    
    // Open tool in new tab
    window.open(tool.referralLink, '_blank');
  };

  const handleMarkToolAsUsed = (toolId: string) => {
    if (user) {
      const tool = tools.find(t => t.id === toolId);
      if (tool && !user.usedTools.includes(toolId)) {
        const { user: updatedUser, activity } = recordToolUse(user, toolId, tool.bonusAmount);
        setUser(updatedUser);
        setActivities(prev => [activity, ...prev]);
      }
    }
  };

  const setTokenBalance = (amount: number) => {
    setTokenBalanceState(amount);
    setStorageTokenBalance(amount);
  };

  const value = {
    user,
    loading,
    tools,
    featuredTool,
    rewardActivities: activities,
    canClaimReward,
    nextClaimTime,
    tokenBalance,
    claimStreak,
    toolStreak,
    handleClaimDailyReward,
    handleUseTool,
    handleMarkToolAsUsed,
    setTokenBalance
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};